import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';
import type {
  AmendPOPayload,
  PoRevision,
  ApprovePOPayload,
  AutoGeneratePOPayload,
  AutoGeneratePOResult,
  CancelPOPayload,
  ReverseReceiptPayload,
  CreatePOPayload,
  NotifyPOPayload,
  NotifyPOResult,
  POListParams,
  POListResponse,
  POStats,
  POStatsParams,
  PurchaseOrder,
  ReceivePOPayload,
  RejectPOPayload,
} from './types';
import type { PoSupply } from './gst-split';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: POStats = {
  total: 0,
  by_status: {
    DRAFT: 0,
    ORDERED: 0,
    PARTIALLY_RECEIVED: 0,
    RECEIVED: 0,
    CANCELLED: 0,
  },
};

export const purchaseOrdersApi = {
  // Deliberately NOT wrapped in withFallback. An empty list and an unreachable
  // list endpoint are indistinguishable once a 404/501 is coerced to `total: 0` —
  // the page renders "No purchase orders yet" for a tenant that has POs, and no
  // error is ever thrown for the page to react to. Let the failure propagate so
  // the list page can show an error + retry instead of a benign empty state.
  // The one search box maps to `search`, not `supplier_name`: the user searches with
  // whatever identifier is in front of them, and on this page that is usually the PO
  // number in the first column. `search` matches PO number OR supplier name server-side.
  list: (params: POListParams) =>
    apiClientPaginated<POListResponse['data']>(
      `/purchase-orders${buildQuery({
        page: params.page,
        limit: params.limit,
        location_id: params.location_id,
        status: params.status || undefined,
        search: params.search,
        supplier_name: params.supplier_name,
        mobile: params.mobile,
        po_number: params.po_number,
        start_date: params.start_date,
        end_date: params.end_date,
      })}`,
    ) as Promise<POListResponse>,

  /** Every PO the list filters match as CSV — written by the API from all pages (T27). */
  exportCsv: (params: Omit<POListParams, 'page' | 'limit'>) =>
    apiBlob(
      `/purchase-orders/export${buildQuery({
        location_id: params.location_id,
        status: params.status || undefined,
        search: params.search,
        supplier_name: params.supplier_name,
        mobile: params.mobile,
        po_number: params.po_number,
        start_date: params.start_date,
        end_date: params.end_date,
      })}`,
    ),

  // Single aggregate call that backs the KPI row — replaces one limit:1 list
  // request per status.
  stats: (params: POStatsParams) =>
    withFallback(
      () =>
        apiClient<POStats>(
          `/purchase-orders/stats${buildQuery({ location_id: params.location_id })}`,
        ),
      EMPTY_STATS,
    ),

  /** Last received unit cost (minor units) per product id; never-received products are left out. */
  /** In `currency` (the PO's); omitted = the tenant currency. */
  lastReceivedCosts: (productIds: string[], currency?: string) =>
    withFallback(
      () =>
        apiClient<Record<string, number>>(
          `/purchase-orders/last-received-costs${buildQuery({ product_ids: productIds.join(','), currency })}`,
        ),
      {} as Record<string, number>,
    ),

  /** GST supply type (CGST + SGST or IGST) for the wizard Review, before the PO exists (QA PU1-004). */
  supplyPreview: (locationId: string, supplierId?: string | null) =>
    apiClient<PoSupply>(
      `/purchase-orders/supply-preview${buildQuery({ location_id: locationId, supplier_id: supplierId || undefined })}`,
    ),

  getById: (id: string) =>
    // Not wrapped in withFallback — a 404 must surface so the detail page leaves its skeleton.
    apiClient<PurchaseOrder>(`/purchase-orders/${id}`),

  /** A4 PDF of the PO. Not wrapped in withFallback — a 404 must surface as an error. */
  downloadPdf: (id: string) => apiBlob(`/purchase-orders/${id}/pdf`),

  create: (payload: CreatePOPayload) =>
    apiClient<PurchaseOrder>('/purchase-orders', {
      method: 'POST',
      body: payload,
    }),

  /** Replace a DRAFT purchase order. The API refuses anything past DRAFT. */
  update: (id: string, payload: CreatePOPayload) =>
    apiClient<PurchaseOrder>(`/purchase-orders/${id}`, {
      method: 'PUT',
      body: payload,
    }),

  /** Amend an approved / partly received PO (lines changed in place, added, removed). */
  amend: (id: string, payload: AmendPOPayload) =>
    apiClient<{ revision: PoRevision; applied: boolean; status?: string }>(`/purchase-orders/${id}/amend`, {
      method: 'PUT',
      body: payload,
    }),

  decideAmendment: (id: string, revisionId: string, approve: boolean, note?: string) =>
    apiClient<{ status: string }>(`/purchase-orders/${id}/amendments/${revisionId}/${approve ? 'approve' : 'reject'}`, {
      method: 'POST',
      body: note ? { note } : {},
    }),

  closeRemaining: (id: string, payload: { item_ids?: string[]; reason: string }) =>
    apiClient<{ status: string }>(`/purchase-orders/${id}/close-remaining`, {
      method: 'POST',
      body: payload,
    }),

  receive: (id: string, payload: ReceivePOPayload) =>
    apiClient<Record<string, unknown>>(`/purchase-orders/${id}/receive`, {
      method: 'POST',
      body: payload,
    }),

  approve: (id: string, payload: ApprovePOPayload) =>
    apiClient<Record<string, unknown>>(`/purchase-orders/${id}/approve`, {
      method: 'POST',
      body: payload,
    }),

  reject: (id: string, payload: RejectPOPayload) =>
    apiClient<Record<string, unknown>>(`/purchase-orders/${id}/reject`, {
      method: 'POST',
      body: payload,
    }),

  /** OWNER only (QA PU3-004): take a goods receipt back, whole or in part. */
  reverseReceipt: (id: string, grnId: string, payload: ReverseReceiptPayload) =>
    apiClient<Record<string, unknown>>(`/purchase-orders/${id}/receipts/${grnId}/reverse`, {
      method: 'POST',
      body: payload,
    }),

  cancel: (id: string, payload: CancelPOPayload) =>
    apiClient<Record<string, unknown>>(`/purchase-orders/${id}/cancel`, {
      method: 'PATCH',
      body: payload,
    }),

  notify: (id: string, payload: NotifyPOPayload) =>
    apiClient<NotifyPOResult>(`/purchase-orders/${id}/notify`, {
      method: 'POST',
      body: payload,
    }),

  autoGenerate: (payload: AutoGeneratePOPayload) =>
    apiClient<AutoGeneratePOResult>('/purchase-orders/auto-generate', {
      method: 'POST',
      body: payload,
    }),

  // ─── DAILY PO template ─────────────────────────────────────────────────────
  getDailyTemplate: () =>
    withFallback(() => apiClient<DailyPoTemplate>('/purchase-orders/daily-template'), null),

  saveDailyTemplate: (payload: SaveDailyPoPayload) =>
    apiClient<DailyPoTemplate>('/purchase-orders/daily-template', { method: 'PUT', body: payload }),

  generateDailyPo: () =>
    apiClient<GenerateDailyPoResult>('/purchase-orders/daily-template/generate', { method: 'POST' }),
};

// ─── DAILY PO types ──────────────────────────────────────────────────────────

export interface DailyPoItem {
  id?: string;
  product_id?: string | null;
  ingredient_id?: string | null;
  item_type?: 'PRODUCT' | 'INGREDIENT';
  name?: string | null;
  quantity: number;
  unit: string;
  unit_cost: number;
}

export interface DailyPoTemplate {
  name: string;
  location_id: string | null;
  location_name: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_phone: string | null;
  supplier_email: string | null;
  items: DailyPoItem[];
}

export interface SaveDailyPoPayload {
  location_id?: string;
  supplier_id?: string;
  supplier_name?: string;
  supplier_phone?: string;
  supplier_email?: string;
  items: { product_id?: string; ingredient_id?: string; quantity: number; unit: string; unit_cost: number }[];
}

export interface GenerateDailyPoResult {
  id: string;
  po_number: string;
  status: string;
  total_amount: number;
  item_count: number;
  /** Lines with no cost yet: an owner's Daily PO then stays a draft until they are priced. */
  cost_to_confirm?: number;
}
